import { type ExprNode } from '@learnforge/demo-math/expr';
import { evaluate } from './evaluate';
import type { Interval } from './domain';

export interface Piece {
  interval: Interval;
  node: ExprNode;
}

export type DiscontinuityKind = 'jump' | 'removable' | 'asymptotic';

export interface Discontinuity {
  x: number;
  leftLim: number;
  rightLim: number;
  kind: DiscontinuityKind;
}

function intervalContains(interval: Interval, x: number): boolean {
  if (interval.openLeft ? x <= interval.start : x < interval.start) return false;
  if (interval.openRight ? x >= interval.end : x > interval.end) return false;
  return true;
}

export function evaluatePiecewise(pieces: readonly Piece[], x: number): number {
  for (const piece of pieces) {
    if (intervalContains(piece.interval, x)) {
      return evaluate(piece.node, x);
    }
  }
  return Number.NaN;
}

export function findDiscontinuities(pieces: readonly Piece[]): Discontinuity[] {
  const out: Discontinuity[] = [];
  const sorted = [...pieces].sort((a, b) => a.interval.start - b.interval.start);
  for (let i = 0; i < sorted.length - 1; i++) {
    const left = sorted[i] as Piece;
    const right = sorted[i + 1] as Piece;
    const boundary = left.interval.end;
    if (Math.abs(boundary - right.interval.start) > 1e-9) continue;
    const leftLim = evaluate(left.node, boundary);
    const rightLim = evaluate(right.node, boundary);
    if (!Number.isFinite(leftLim) || !Number.isFinite(rightLim)) {
      out.push({ x: boundary, leftLim, rightLim, kind: 'asymptotic' });
    } else if (Math.abs(leftLim - rightLim) > 1e-9) {
      out.push({ x: boundary, leftLim, rightLim, kind: 'jump' });
    }
  }
  return out;
}
